import React, { useState, useEffect } from 'react';
import './ArticleDetailsModal.css';

const countWordsFromArticleContent = (content) => {
  if (!content) return { totalWords: 0, uniqueWords: 0 };
  let cleanText = String(content)
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/\s+/g, ' ')
    .trim();
  const words = cleanText.toLowerCase().match(/[\p{L}]{2,}/gu) || [];
  return { totalWords: words.length, uniqueWords: new Set(words).size };
};

const mergeContentWordCounts = (analytics, article) => {
  if (!analytics || (analytics.totalWords && analytics.totalWords > 0)) return analytics;
  const local = countWordsFromArticleContent(article && article.content);
  if (local.totalWords <= 0) return analytics;
  return {
    ...analytics,
    totalWords: local.totalWords,
    uniqueWords: local.uniqueWords
  };
};

const ArticleDetailsModal = ({ article, isOpen, onClose, onStartReading, isLocked = false, onUnlockClick, userId }) => {
  const [articleAnalytics, setArticleAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = `${process.env.REACT_APP_API_URL || ''}/api/vocabulary-analytics`;
  const rawUserId = userId != null && userId !== '' ? String(userId).trim() : '';
  const currentUserId = /^\d+$/.test(rawUserId) ? rawUserId : null;

  // Mock data for demonstration - fallback if API is not available
  const mockAnalytics = {
    totalWords: 684,
    uniqueWords: 378,
    firstReadingStats: {
      unknownWords: 32,
      percentage: 8.5,
      date: '2024-01-15'
    },
    latestReadingStats: {
      unknownWords: 15,
      percentage: 3.9,
      date: '2024-01-20'
    },
    improvement: {
      percentage: 53,
      direction: 'improved'
    },
    wordCategories: {
      A1: { unknown: 2, total: 45, percentage: 4.4 },
      A2: { unknown: 5, total: 67, percentage: 7.5 },
      B1: { unknown: 15, total: 89, percentage: 16.9 },
      B2: { unknown: 8, total: 45, percentage: 17.8 },
      C1: { unknown: 2, total: 12, percentage: 16.7 }
    },
    retention: {
      masteredWords: 20,
      totalNewWords: 32,
      percentage: 62.5
    },
    spacedRepetition: {
      nextReview: '2024-01-23',
      lastPractice: '2024-01-20',
      daysUntilNext: 3
    },
    readingHistory: [
      { date: '2024-01-15', unknownWords: 32, percentage: 8.5 },
      { date: '2024-01-17', unknownWords: 28, percentage: 7.4 },
      { date: '2024-01-20', unknownWords: 15, percentage: 3.9 }
    ]
  };

  // Determine if this is an exam article
  const isExamArticle = article && article.exam_type; // exam articles have exam_type, regular articles have school/year
  
  // Fetch analytics data from API
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUserId) {
        await createInitialSession();
        return;
      }
      
      console.log(`🔍 Fetching analytics for ${isExamArticle ? 'EXAM' : 'REGULAR'} article - User: ${currentUserId}, Article: ${article.id}`);
      
      // Use different endpoint for exam articles
      const endpoint = isExamArticle 
        ? `${API_BASE_URL}/exam-analytics/${currentUserId}/${article.id}`
        : `${API_BASE_URL}/analytics/${currentUserId}/${article.id}`;
      
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        if (response.status === 404) {
          // No analytics data exists yet - this is normal for new articles
          console.log('📊 No analytics data found - creating initial session');
          await createInitialSession();
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`✅ Analytics data loaded for ${isExamArticle ? 'exam' : 'regular'} article:`, data);
      const merged = mergeContentWordCounts(data, article);
      setArticleAnalytics(merged);

      if ((!data.totalWords || data.totalWords === 0) && !isExamArticle && article?.id) {
        const analyzeEndpoint = `${API_BASE_URL}/analyze-article/${article.id}`;
        fetch(analyzeEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
          .then((analyzeResponse) => (analyzeResponse.ok ? analyzeResponse.json() : null))
          .then((analyzeData) => {
            if (analyzeData && analyzeData.totalWords > 0) {
              setArticleAnalytics((prev) => ({
                ...(prev || {}),
                totalWords: analyzeData.totalWords,
                uniqueWords: analyzeData.uniqueWords
              }));
            }
          })
          .catch((backendError) => {
            console.warn('⚠️ Backend analysis failed:', backendError.message);
          });
      }
    } catch (err) {
      console.warn('⚠️ Analytics API error:', err.message);
      console.log('🔄 Falling back to initial session creation...');
      setError('Demo Mode - Backend not connected');
      await createInitialSession();
    } finally {
      setLoading(false);
    }
  };

  // Create initial session for articles without analytics data
  const createInitialSession = async () => {
    try {
      // First, let's count words directly from the article content
      let totalWords = 0;
      let uniqueWords = 0;
      
      if (article.content) {
        const counted = countWordsFromArticleContent(article.content);
        totalWords = counted.totalWords;
        uniqueWords = counted.uniqueWords;
        
        console.log(`📝 Frontend word analysis: ${totalWords} total words, ${uniqueWords} unique words`);
      } else {
        console.warn('⚠️ No article content available for word counting');
      }
      
      // Ensure we have reasonable numbers (fallback if both methods fail)
      if (totalWords === 0) {
        totalWords = 50; // Minimal fallback
        uniqueWords = 30;
        console.warn('⚠️ Using minimal fallback word counts');
      }

      // Also try to analyze the article on the backend
      try {
        // Use different endpoint for exam articles
        const analyzeEndpoint = isExamArticle 
          ? `${API_BASE_URL}/analyze-exam-article/${article.id}`
          : `${API_BASE_URL}/analyze-article/${article.id}`;
        
        console.log(`🔧 Calling backend analysis: ${analyzeEndpoint}`);
        
        const analyzeResponse = await fetch(analyzeEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (analyzeResponse.ok) {
          const analyzeData = await analyzeResponse.json();
          console.log(`✅ Backend analysis: ${analyzeData.totalWords} total, ${analyzeData.uniqueWords} unique`);
          
          // Use backend results if they seem reasonable
          if (analyzeData.totalWords && analyzeData.totalWords > 0) {
            totalWords = analyzeData.totalWords;
            uniqueWords = analyzeData.uniqueWords;
            console.log(`🎯 Using backend word counts: ${totalWords} total, ${uniqueWords} unique`);
          }
        } else {
          console.warn(`⚠️ Backend analysis failed with status: ${analyzeResponse.status}`);
        }
      } catch (backendError) {
        console.warn('⚠️ Backend analysis failed, using frontend count:', backendError.message);
      }

      // Create a basic analytics structure for articles not started (no fake reading dates)
      const initialAnalytics = {
        totalWords: totalWords,
        uniqueWords: uniqueWords,
        readCompletely: false,
        firstReadingStats: {
          unknownWords: 0,
          percentage: 0,
          date: null
        },
        latestReadingStats: {
          unknownWords: 0,
          percentage: 0,
          date: null
        },
        improvement: {
          percentage: 0,
          direction: 'none'
        },
        wordCategories: {
          A1: { unknown: 0, total: 0, percentage: 0 },
          A2: { unknown: 0, total: 0, percentage: 0 },
          B1: { unknown: 0, total: 0, percentage: 0 },
          B2: { unknown: 0, total: 0, percentage: 0 },
          C1: { unknown: 0, total: 0, percentage: 0 }
        },
        retention: {
          masteredWords: 0,
          totalNewWords: 0,
          percentage: 0
        },
        spacedRepetition: {
          nextReview: null,
          lastPractice: null,
          daysUntilNext: null
        },
        readingHistory: []
      };

      console.log('📋 Created initial analytics structure');
      setArticleAnalytics(initialAnalytics);
    } catch (err) {
      console.warn('⚠️ Could not create initial session:', err.message);
      setError('Demo Mode - Backend not connected');
      setArticleAnalytics({
        totalWords: 0,
        uniqueWords: 0,
        readCompletely: false,
        firstReadingStats: { unknownWords: 0, percentage: 0, date: null },
        latestReadingStats: { unknownWords: 0, percentage: 0, date: null },
        improvement: { percentage: 0, direction: 'none' },
        wordCategories: { A1: { unknown: 0, total: 0, percentage: 0 }, A2: { unknown: 0, total: 0, percentage: 0 }, B1: { unknown: 0, total: 0, percentage: 0 }, B2: { unknown: 0, total: 0, percentage: 0 }, C1: { unknown: 0, total: 0, percentage: 0 } },
        retention: { masteredWords: 0, totalNewWords: 0, percentage: 0 },
        spacedRepetition: { nextReview: null, lastPractice: null, daysUntilNext: null },
        readingHistory: []
      });
    }
  };

  useEffect(() => {
    if (isOpen && article) {
      console.log(`🔎 Modal opened for ${isExamArticle ? 'EXAM' : 'REGULAR'} article: "${article.title}"`);
      fetchAnalytics();
    }
  }, [isOpen, article, isExamArticle]);

  // Track word interaction when user clicks a word
  const trackWordInteraction = async (wordText, context = '') => {
    if (!currentUserId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/vocabulary-analytics/word-interaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUserId,
          articleId: article.id,
          wordText: wordText,
          interactionType: 'click',
          context: context
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`📝 Word interaction tracked: ${wordText}`);
        // Refresh analytics after word interaction
        fetchAnalytics();
        return result;
      }
    } catch (err) {
      console.warn('⚠️ Could not track word interaction:', err.message);
    }
  };

  // End reading session
  const endReadingSession = async (readingTime, completionPercentage) => {
    if (!currentUserId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/end-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUserId,
          articleId: article.id,
          readingTime: readingTime,
          completionPercentage: completionPercentage
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Reading session ended: ${result.wordsClicked} words clicked`);
        // Refresh analytics after session ends
        fetchAnalytics();
        return result;
      }
    } catch (err) {
      console.warn('⚠️ Could not end reading session:', err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysFromNow = (dateString) => {
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    return `in ${diffDays} days`;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return '#4CAF50';
    if (percentage >= 60) return '#FFC107';
    if (percentage >= 40) return '#FF9800';
    return '#f44336';
  };

  if (!isOpen) return null;

  return (
    <div className="article-details-overlay" onClick={onClose}>
      <div className="article-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="details-header">
          <div className="details-title-section">
            <h2 className="details-title">{article?.title}</h2>
            <p className="details-subtitle">
              Reading Progress & Vocabulary Analytics
              {error && <span className="demo-indicator"> (Demo Mode)</span>}
            </p>
          </div>
          <button className="details-close-btn" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>

        {isLocked && onUnlockClick && (
          <div className="details-unlock-banner">
            <span className="material-icons">lock</span>
            <p>Unlock 120+ articles with a subscription to read this and all content.</p>
            <button type="button" className="details-unlock-btn" onClick={onUnlockClick}>
              View subscription
            </button>
          </div>
        )}

        {loading ? (
          <div className="details-loading">
            <div className="loading-spinner"></div>
            <p>Loading analytics...</p>
          </div>
        ) : (
          <div className="details-content">
            {/* Read-complete status: student has finished the article (≥90% in at least one session) */}
            {typeof articleAnalytics?.readCompletely === 'boolean' && (
              <div className="details-section read-complete-badge" style={{ marginBottom: 12 }}>
                <span className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="material-icons">{articleAnalytics.readCompletely ? 'check_circle' : 'radio_button_unchecked'}</i>
                  {articleAnalytics.readCompletely ? 'Article read completely' : 'Article not read completely yet'}
                </span>
                <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.9 }}>
                  {articleAnalytics.readCompletely
                    ? 'Student met the completion threshold (scroll + time + focus + word interactions).'
                    : 'Completion uses a weighted score: scroll ≥90%, reading time, tab focus time, and word clicks.'}
                </p>
              </div>
            )}
            {/* Article Vocabulary Stats */}
            <div className="details-section">
              <h3 className="section-title">
                <i className="material-icons">analytics</i>
                Article Vocabulary Stats
              </h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📝</div>
                  <div className="stat-content">
                    <div className="stat-number">{articleAnalytics?.totalWords || 0}</div>
                    <div className="stat-label">Total Words</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🆔</div>
                  <div className="stat-content">
                    <div className="stat-number">{articleAnalytics?.uniqueWords || 0}</div>
                    <div className="stat-label">Unique Words</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">❓</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {articleAnalytics?.firstReadingStats?.unknownWords || 0}
                    </div>
                    <div className="stat-label">
                      First Reading Unknown ({articleAnalytics?.firstReadingStats?.percentage || 0}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Improvement Tracking */}
            <div className="details-section improvement-tracking-section">
              <h3 className="section-title">
                <i className="material-icons">trending_up</i>
                Improvement Tracking
              </h3>
              <div className="improvement-grid">
                <div className="improvement-card">
                  <div className="improvement-header">
                    <span className="improvement-label">First Reading</span>
                    <span className="improvement-date">
                      {articleAnalytics?.firstReadingStats?.date ? 
                        formatDate(articleAnalytics.firstReadingStats.date) : 
                        'No data'
                      }
                    </span>
                  </div>
                  <div className="improvement-stats">
                    <span className="unknown-count">
                      {articleAnalytics?.firstReadingStats?.unknownWords || 0} words
                    </span>
                    <span className="unknown-percentage">
                      {articleAnalytics?.firstReadingStats?.percentage || 0}% unknown
                    </span>
                  </div>
                </div>
                
                <div className="improvement-arrow">
                  <i className="material-icons">arrow_forward</i>
                  <span className="improvement-badge positive">
                    -{articleAnalytics?.improvement?.percentage || 0}%
                  </span>
                </div>
                
                <div className="improvement-card">
                  <div className="improvement-header">
                    <span className="improvement-label">Latest Reading</span>
                    <span className="improvement-date">
                      {articleAnalytics?.latestReadingStats?.date ? 
                        formatDate(articleAnalytics.latestReadingStats.date) : 
                        'No data'
                      }
                    </span>
                  </div>
                  <div className="improvement-stats">
                    <span className="unknown-count">
                      {articleAnalytics?.latestReadingStats?.unknownWords || 0} words
                    </span>
                    <span className="unknown-percentage">
                      {articleAnalytics?.latestReadingStats?.percentage || 0}% unknown
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Word Category Breakdown */}
            <div className="details-section">
              <h3 className="section-title">
                <i className="material-icons">category</i>
                Word Difficulty Breakdown (CEFR Levels)
              </h3>
              <div className="category-breakdown">
                {Object.entries(articleAnalytics?.wordCategories || {}).map(([level, data]) => (
                  <div key={level} className="category-item">
                    <div className="category-header">
                      <span className={`category-level ${level.toLowerCase()}`}>{level}</span>
                      <span className="category-percentage">{Number(data.percentage || 0).toFixed(1)}% unknown</span>
                    </div>
                    <div className="category-progress">
                      <div 
                        className="category-bar"
                        style={{ 
                          width: `${data.percentage}%`,
                          backgroundColor: getProgressColor(100 - data.percentage)
                        }}
                      ></div>
                    </div>
                    <div className="category-details">
                      {data.unknown} / {data.total} words unknown
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Retention Progress */}
            <div className="details-section">
              <h3 className="section-title">
                <i className="material-icons">psychology</i>
                Retention Progress
              </h3>
              <div className="retention-container">
                <div className="retention-goal">
                  <p>Goal: Master all {articleAnalytics?.retention?.totalNewWords || 0} new words</p>
                </div>
                <div className="retention-progress">
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar"
                      style={{ 
                        width: `${articleAnalytics?.retention?.percentage || 0}%`,
                        backgroundColor: getProgressColor(articleAnalytics?.retention?.percentage || 0)
                      }}
                    ></div>
                  </div>
                  <div className="progress-text">
                    {articleAnalytics?.retention?.masteredWords || 0} / {articleAnalytics?.retention?.totalNewWords || 0} words 
                    ({articleAnalytics?.retention?.percentage || 0}% mastered)
                  </div>
                </div>
              </div>
            </div>

            {/* Spaced Repetition */}
            <div className="details-section">
              <h3 className="section-title">
                <i className="material-icons">schedule</i>
                Spaced Repetition & Next Review
              </h3>
              <div className="spaced-repetition-card">
                <div className="sr-item">
                  <div className="sr-icon">🎯</div>
                  <div className="sr-content">
                    <div className="sr-label">Next Review Due</div>
                    <div className="sr-value next-review">
                      {articleAnalytics?.spacedRepetition?.nextReview ? 
                        getDaysFromNow(articleAnalytics.spacedRepetition.nextReview) : 
                        'Not scheduled'
                      }
                    </div>
                  </div>
                </div>
                <div className="sr-item">
                  <div className="sr-icon">📅</div>
                  <div className="sr-content">
                    <div className="sr-label">Last Practice</div>
                    <div className="sr-value">
                      {articleAnalytics?.spacedRepetition?.lastPractice ? 
                        formatDate(articleAnalytics.spacedRepetition.lastPractice) : 
                        'No practice yet'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="details-section">
              <h3 className="section-title">
                <i className="material-icons">flash_on</i>
                Quick Actions
              </h3>
              <div className="quick-actions">
                {isLocked && onUnlockClick ? (
                  <button type="button" className="action-btn primary" onClick={onUnlockClick}>
                    <i className="material-icons">lock_open</i>
                    Unlock to read
                  </button>
                ) : (
                  <button className="action-btn primary" onClick={() => {
                    console.log('📖 Starting reading session for article:', article.title);
                    onStartReading(article);
                    onClose();
                  }}>
                    <i className="material-icons">visibility</i>
                    Re-read Article
                  </button>
                )}
                <button className="action-btn secondary">
                  <i className="material-icons">style</i>
                  Review Words (Flashcards)
                </button>
                <button className="action-btn secondary">
                  <i className="material-icons">quiz</i>
                  Take Retention Quiz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleDetailsModal;
